import axios, { AxiosInstance } from 'axios'
import { AxiosErrors } from '../utils/constants'
import { AXIOS_METHODS } from '../utils/constants/enum'
import {
    getLocalState,
    hasLocalState,
    removeLocalState,
    setLocalState,
} from '../utils/local-storage'
import { Tokens } from '../utils/types/auth'
import { refreshTokenFromMiddleLayer } from './auth'
import { MixPanel } from './mixpanelActions'
import { ICountry } from 'country-state-city/lib/interface'
import { CountryDetails } from '../utils/constants/country-details'

interface AxiosResponseType<DataType> {
    success: boolean
    data: DataType
    message: string
    messageCode?: string
}

interface AxiosHsResponseType<DataType> {
    status: number
    data: DataType
    statusText: string
}

export interface IUserLocationDetails {
    ISO3166_2_lvl4: string
    city: string
    country: string
    country_code: string
    postcode: string
    state: string
    state_district: string
    suburb: string
}

class AxiosClass {
    private _tokens: any = null

    private _syndeoInstance: AxiosInstance | null = null

    get syndeoInstance() {
        if (!this._syndeoInstance && hasLocalState('auth-token')) {
            this.initSyndeoInstance(getLocalState('auth-token')!)
        }
        if (!this._syndeoInstance || !this._tokens) {
            throw new Error(AxiosErrors.ER01)
        }
        return this._syndeoInstance
    }

    destroySyndeoInstance() {
        removeLocalState('auth-token')

        this._tokens = null
        this._syndeoInstance = null
    }

    initSyndeoInstance(tokens: Tokens) {
        setLocalState('auth-token', tokens)
        this._tokens = tokens
        let apiRequest: any
        this._syndeoInstance = axios.create({
            baseURL: process.env.REACT_APP_SYNDEO_API_BASE_URL,
            headers: {
                Authorization: `Bearer ${this._tokens.jwtToken}`,
            },
        })

        this._syndeoInstance.interceptors.request.use((request) => {
            apiRequest = request
            return apiRequest
        })

        this._syndeoInstance.interceptors.response.use(
            (response) => {
                MixPanel.actions.track('CSE Network Log', {
                    ApiEndPoint: response?.config?.url,
                    ApiRequest: response?.config?.data
                        ? response?.config?.data
                        : 'No Payload',
                    ApiResponse: response?.data,
                    Message: response?.data?.message,
                    Method: response?.config?.method?.toUpperCase(),
                    Status: response?.status,
                    ResponseType: 'Success',
                })
                return response
            },
            async (error) => {
                MixPanel.actions.track('CSE Network Log', {
                    ApiEndPoint: apiRequest?.url,
                    ApiRequest: apiRequest?.data,
                    ApiResponse: error,
                    Message: error?.message,
                    Method: apiRequest?.method.toUpperCase(),
                    Status: error?.status,
                    ResponseType: 'Error',
                })
                if (401 === error.response?.status && this._tokens) {
                    try {
                        const token = await refreshTokenFromMiddleLayer(
                            this._tokens.middleLayerRefreshToken
                        )
                        this.initmiddleLayerInstance({
                            ...token,
                            refreshToken: this._tokens.refreshToken,
                            middleLayerRefreshToken:
                                this._tokens.middleLayerRefreshToken,
                        })
                        this.initSyndeoInstance({
                            ...token,
                            refreshToken: this._tokens.refreshToken,
                            middleLayerRefreshToken:
                                this._tokens.middleLayerRefreshToken,
                        })
                        const config = error.config
                        config.headers = {
                            ...config.headers,
                            Authorization: `Bearer ${token.accessToken}`,
                        }
                        return axios.request(config)
                    } catch (e) {
                        return Promise.reject(error)
                    }
                } else {
                    return Promise.reject(error)
                }
            }
        )
    }

    async csApiCall<PayloadType, DataType>(
        query: PayloadType
    ): Promise<AxiosResponseType<DataType>> {
        try {
            const data = JSON.stringify({
                query: `query MyQuery ${query}`,
                variables: {},
            })
            const headers = {
                access_token: `${process.env.REACT_APP_CS_DELIVERY_TOKEN}`,
                'Content-Type': 'application/json',
            }
            const response = await axios({
                method: AXIOS_METHODS.POST,
                url: `${process.env.REACT_APP_CS_API_URL}`,
                headers,
                data,
            })
            return response.data
        } catch (error) {
            console.error(error) // as per sonar cloud
            throw error
        }
    }

    async hsApiCall<PayloadType, DataType>(
        request: PayloadType,
        portalId: string,
        formId: string
    ): Promise<AxiosHsResponseType<DataType>> {
        try {
            const data = {
                portalId,
                formId,
                fields: request,
            }
            const headers = {
                'Content-Type': 'application/json',
            }
            const response = await axios({
                method: AXIOS_METHODS.POST,
                url: `${process.env.REACT_APP_HUB_SPOT_API_URL}/${portalId}/${formId}`,
                headers,
                data,
            })
            return response.data
        } catch (error) {
            console.error(error) // as per sonar cloud
            throw error
        }
    }

    async csCdnApiCall<DataType>(
        method: AXIOS_METHODS,
        url: string
    ): Promise<DataType> {
        try {
            const headers = {
                api_key: `${process.env.REACT_APP_CS_API_KEY}`,
                access_token: `${process.env.REACT_APP_CS_DELIVERY_TOKEN}`,
            }
            const response = await axios({
                method,
                headers,
                url: `${process.env.REACT_APP_CS_CDN_API_URL}${url}`,
            })
            return response.data
        } catch (error) {
            console.error(error) // as per sonar cloud
            throw error
        }
    }

    async syndeoApiCall<PayloadType, DataType>(
        method: AXIOS_METHODS,
        url: string,
        params?: PayloadType | undefined,
        dataSet?: PayloadType | undefined
    ): Promise<DataType | undefined> {
        try {
            const headers = {
                'Content-Type': 'application/json',
            }

            const response = await axios({
                method: method,
                url: `${process.env.REACT_APP_SYNDEO_API_BASE_URL}${url}${
                    params ? params : ''
                }`,
                headers: headers,
                data: dataSet,
            })
            MixPanel.actions.track('CSE Network Log', {
                ApiEndPoint: url,
                ApiRequest: dataSet,
                ApiResponse: response?.data,
                Message: response?.data?.message,
                Method: method,
                Status: response?.status,
                ResponseType: 'Success',
            })
            if (response?.data) return response.data
            return undefined
        } catch (error: any) {
            console.error(error) // as per sonar cloud
            MixPanel.actions.track('CSE NetworkLog', {
                ApiEndPoint: url,
                ApiRequest: dataSet,
                ApiResponse: error,
                Message: error?.message,
                Method: method,
                Status: error?.status,
                ResponseType: 'Error',
            })
            throw error
        }
    }

    //MiddleLayer

    private _middleLayerInstance: AxiosInstance | null = null

    get middleLayerInstance() {
        if (!this._middleLayerInstance && hasLocalState('auth-token')) {
            this.initmiddleLayerInstance(getLocalState('auth-token')!)
        }
        if (!this._middleLayerInstance || !this._tokens) {
            throw new Error(AxiosErrors.ER01)
        }
        return this._middleLayerInstance
    }

    initmiddleLayerInstance(tokens: Tokens) {
        setLocalState('auth-token', tokens)
        this._tokens = tokens
        let apiRequest: any
        this._middleLayerInstance = axios.create({
            method: AXIOS_METHODS.POST,
            baseURL: process.env.REACT_APP_CODITAS_MIDDLELAYER_URL,
            headers: {
                platform: 'website',
                Authorization: `Bearer ${this._tokens.jwtToken}`,
            },
        })

        this._middleLayerInstance.interceptors.request.use((request) => {
            apiRequest = request
            return request
        })

        this._middleLayerInstance.interceptors.response.use(
            async (response) => {
                MixPanel.actions.track('Middle Layer Network Log', {
                    ApiEndPoint: response?.config?.url,
                    ApiRequest: response?.config?.data
                        ? JSON.parse(response?.config?.data)
                        : 'No Payload',
                    ApiResponse: response?.data,
                    Message: response?.data?.message,
                    Method: response?.config?.method?.toUpperCase(),
                    Status: response?.status,
                    ResponseType: 'Success',
                })
                return response.data
            },
            async (error) => {
                MixPanel.actions.track('Middle Layer Network Log', {
                    ApiEndPoint: apiRequest?.url,
                    ApiRequest: apiRequest?.data,
                    ApiResponse: error,
                    Message: error?.message,
                    Method: apiRequest?.method?.toUpperCase(),
                    Status: error?.status,
                    ResponseType: 'Error',
                })
                if (401 === error.response?.status && this._tokens) {
                    try {
                        const token = await refreshTokenFromMiddleLayer(
                            this._tokens.middleLayerRefreshToken
                        )
                        this.initmiddleLayerInstance({
                            ...token,
                            refreshToken: this._tokens.refreshToken,
                            middleLayerRefreshToken:
                                this._tokens.middleLayerRefreshToken,
                        })
                        this.initSyndeoInstance({
                            ...token,
                            refreshToken: this._tokens.refreshToken,
                            middleLayerRefreshToken:
                                this._tokens.middleLayerRefreshToken,
                        })
                        const config = error.config
                        config.headers = {
                            ...config.headers,
                            Authorization: `Bearer ${token.accessToken}`,
                        }
                        return (await axios.request(config)).data
                    } catch (e) {
                        return Promise.reject(error)
                    }
                } else {
                    return Promise.reject(error)
                }
            }
        )
    }

    destroyMiddleLayerInstance() {
        removeLocalState('auth-token')

        this._tokens = null
        this._middleLayerInstance = null
        MixPanel.actions.reset()
    }

    async middleLayerApiCall<PayloadType, DataType>(
        method: AXIOS_METHODS,
        url?: string,
        params?: PayloadType | undefined,
        dataSet?: PayloadType | undefined
    ): Promise<DataType | undefined> {
        try {
            const headers = {
                'Content-Type': 'application/json',
                platform: 'website',
            }

            let constructedUrl = `${process.env.REACT_APP_CODITAS_MIDDLELAYER_URL}`
            if (url) {
                constructedUrl += url
            }
            if (params) {
                constructedUrl += params
            }

            const response = await axios({
                method: method,
                url: constructedUrl,
                headers,
                data: dataSet,
            })
            MixPanel.actions.track('Middle Layer Network Log', {
                ApiEndPoint: url,
                ApiRequest: dataSet,
                ApiResponse: response?.data,
                Message: response?.data?.message,
                Method: method,
                Status: response?.status,
                ResponseType: 'Success',
            })

            if (response?.data) {
                return response.data
            }
            return undefined
        } catch (error: any) {
            MixPanel.actions.track('Middle Layer Network Log', {
                ApiEndPoint: url,
                ApiRequest: dataSet,
                ApiResponse: error,
                Message: error?.message,
                Method: method,
                Status: error?.status,
                ResponseType: 'Error',
            })
            console.error(error)
            throw error
        }
    }

    async getLocationDetails(
        lat: number,
        lng: number
    ): Promise<IUserLocationDetails> {
        try {
            const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
            const response: any = await axios.get(url)
            return response.data.address
        } catch (e) {
            throw e
        }
    }
}

export const Axios = new AxiosClass()
